(() => {
  if (window.ConsoleAddons?.version === 1) {
    window.ConsoleAddons.initAll();
    return;
  }

  const ROOT_SELECTOR = '[data-console-addons]';
  const isEnglish = String(document.documentElement.lang || '').toLowerCase().startsWith('en');

  const normalize = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const numericId = (value) => {
    const match = String(value || '').match(/(\d+)$/);
    return match ? Number(match[1]) : null;
  };

  const parseConfig = (root) => {
    const source = root.querySelector('[data-console-addon-config]')?.textContent?.trim();
    if (!source) return null;

    try {
      const parsed = JSON.parse(source);
      const config = typeof parsed === 'string' ? JSON.parse(parsed) : parsed;

      if (root.dataset.xlBatteryEligible === 'true') {
        const batteryVariantId = numericId(root.dataset.xlBatteryVariantId);
        const batteryPrice = Number(root.dataset.xlBatteryPrice || 0);
        const batteryAvailable = root.dataset.xlBatteryAvailable !== 'false';

        if (batteryVariantId && batteryPrice > 0) {
          config.groups ||= [];
          config.variants ||= {};

          if (!config.groups.some((group) => group.key === 'battery')) {
            config.groups.push({
              key: 'battery',
              label: 'Akku',
              property: 'Akku',
              base_value: 'Standard',
              options: [{ value: 'XL Akku', label: 'XL Akku' }],
            });
          }

          Object.values(config.variants).forEach((variant) => {
            variant.battery ||= {};
            variant.battery['XL Akku'] ||= {
              variant_id: batteryVariantId,
              price: batteryPrice,
              available: batteryAvailable,
            };
          });
        }
      }

      return config;
    } catch (error) {
      console.error('Console add-on configuration is invalid.', error);
      return null;
    }
  };

  class ConsoleAddons {
    constructor(root) {
      this.root = root;
      this.config = parseConfig(root);
      this.productInfo = root.closest('product-info');
      this.formId = root.dataset.productFormId;
      this.sectionId = root.dataset.sectionId;
      this.groupsRoot = root.querySelector('[data-console-addon-groups]');
      this.selected = {};
      this.currentVariant = this.readSelectedVariant();
      this.unsubscribeVariantChange = null;
    }

    connect() {
      if (!this.config || !this.groupsRoot || !this.productInfo) return false;

      (this.config.groups || []).forEach((group) => {
        this.selected[group.key] = group.base_value;
      });

      const changedControl = this.forceNativeBaseOptions();
      this.renderGroups();
      this.renderPrice();

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (String(event.data?.sectionId) !== String(this.sectionId)) return;
          this.currentVariant = event.data?.variant || this.readSelectedVariant();
          this.forceNativeBaseOptions(false);
          this.renderGroups();
          this.renderPrice();
        });
      }

      if (changedControl) {
        queueMicrotask(() => changedControl.dispatchEvent(new Event('change', { bubbles: true })));
      }

      this.root.dataset.consoleAddonsReady = 'true';
      return true;
    }

    readSelectedVariant() {
      const source = this.productInfo?.querySelector('variant-selects [data-selected-variant]')?.textContent?.trim();
      if (!source) return null;

      try {
        return JSON.parse(source);
      } catch (_error) {
        return null;
      }
    }

    get form() {
      return document.getElementById(this.formId);
    }

    get variantId() {
      return numericId(this.form?.querySelector('[name="id"]')?.value || this.currentVariant?.id);
    }

    variantConfig() {
      return this.config.variants?.[String(this.variantId)] || {};
    }

    forceNativeBaseOptions(allowDispatch = true) {
      const variantSelects = this.productInfo.querySelector('variant-selects');
      if (!variantSelects) return null;

      let changedControl = null;

      Object.entries(this.config.base_options || {}).forEach(([optionName, baseValue]) => {
        const optionRoot = variantSelects.querySelector(`[data-option-name="${normalize(optionName)}"]`);
        if (!optionRoot) return;

        optionRoot.hidden = true;
        optionRoot.setAttribute('aria-hidden', 'true');

        const radios = Array.from(optionRoot.querySelectorAll('input[type="radio"]'));
        const radio = radios.find((input) => normalize(input.value) === normalize(baseValue));
        if (radio && !radio.checked) {
          radio.checked = true;
          changedControl = radio;
        }

        const select = optionRoot.querySelector('select');
        const option = Array.from(select?.options || []).find(
          (candidate) => normalize(candidate.value) === normalize(baseValue)
        );
        if (select && option && select.value !== option.value) {
          Array.from(select.options).forEach((candidate) => candidate.removeAttribute('selected'));
          option.selected = true;
          option.setAttribute('selected', 'selected');
          changedControl = select;
        }
      });

      return allowDispatch ? changedControl : null;
    }

    addonFor(group, value) {
      if (value === group.base_value) return null;
      return this.variantConfig()?.[group.key]?.[value] || null;
    }

    isSelectable(group, value) {
      if (value === group.base_value) return true;
      const addon = this.addonFor(group, value);
      return Boolean(numericId(addon?.variant_id) && addon?.available !== false);
    }

    formatMoney(cents) {
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(Number(cents || 0), this.root.dataset.moneyFormat);
      }

      return (Number(cents || 0) / 100).toLocaleString(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: this.root.dataset.currency || 'EUR',
      });
    }

    optionLabel(group, option) {
      const label = option.label || option.value;
      if (!isEnglish) return label;
      if (group.key === 'battery' && normalize(option.value) === 'xl-akku') return 'XL battery';
      return label;
    }

    groupLabel(group) {
      if (!isEnglish) return group.label || group.property || group.key;
      if (group.key === 'storage') return 'Storage';
      if (group.key === 'hardware') return 'Hardware mod';
      if (group.key === 'battery') return 'Battery';
      return group.label || group.property || group.key;
    }

    renderGroups() {
      this.groupsRoot.replaceChildren();

      (this.config.groups || []).forEach((group, groupIndex) => {
        if (!group?.key || !group?.base_value) return;

        if (!this.isSelectable(group, this.selected[group.key])) {
          this.selected[group.key] = group.base_value;
        }

        const fieldset = document.createElement('fieldset');
        fieldset.className = 'js product-form__input product-form__input--pill console-addons__group';
        fieldset.dataset.consoleAddonGroup = group.key;

        const legend = document.createElement('legend');
        legend.className = 'form__label';
        legend.textContent = this.groupLabel(group);
        fieldset.append(legend);

        const options = [
          { value: group.base_value, label: group.base_label || group.base_value },
          ...(group.options || []),
        ];

        options.forEach((option, optionIndex) => {
          const input = document.createElement('input');
          const inputId = `ConsoleAddon-${this.sectionId}-${groupIndex}-${optionIndex}`;
          input.type = 'radio';
          input.id = inputId;
          input.name = `console_addon_${this.sectionId}_${group.key}`;
          input.value = option.value;
          input.checked = this.selected[group.key] === option.value;
          input.disabled = !this.isSelectable(group, option.value);

          const label = document.createElement('label');
          label.htmlFor = inputId;
          label.textContent = this.optionLabel(group, option);

          input.addEventListener('change', () => {
            if (!input.checked) return;
            this.selected[group.key] = option.value;
            this.renderPrice();
            this.root.dispatchEvent(
              new CustomEvent('console-addons:change', {
                bubbles: true,
                detail: { group: group.key, value: option.value },
              })
            );
          });

          fieldset.append(input, label);
        });

        this.groupsRoot.append(fieldset);
      });
    }

    selectedAddons() {
      return (this.config.groups || []).flatMap((group) => {
        const value = this.selected[group.key] || group.base_value;
        const addon = this.addonFor(group, value);
        if (!addon || !this.isSelectable(group, value)) return [];
        return [{ group, value, addon }];
      });
    }

    parentProperties(properties = {}) {
      const merged = window.VRExperiments?.mergeProperties?.(properties) || { ...properties };
      (this.config.groups || []).forEach((group) => {
        const value = this.selected[group.key] || group.base_value;
        merged[group.property || group.label || group.key] = value;
      });
      return merged;
    }

    buildLines({ variantId, quantity = 1, properties = {}, sellingPlan = null }) {
      const parentVariantId = numericId(variantId || this.variantId);
      const parent = {
        id: parentVariantId,
        quantity: Number(quantity || 1),
        properties: this.parentProperties(properties),
      };

      if (sellingPlan) parent.selling_plan = numericId(sellingPlan);

      const children = this.selectedAddons().map(({ group, addon }) => ({
        id: numericId(addon.variant_id),
        quantity: Number(quantity || 1),
        parent_id: parentVariantId,
        properties: {
          _bundle_hidden: 'true',
          _bundle_kind: group.key,
        },
      }));

      return [parent, ...children];
    }

    renderPrice() {
      const priceRoot = document.getElementById(`price-${this.sectionId}`);
      if (!priceRoot) return;

      const priceData = priceRoot.querySelector('[data-console-addon-price-data]');
      const basePrice = Number(this.currentVariant?.price ?? priceData?.dataset.basePrice ?? 0);
      const compareAtPrice = Number(
        this.currentVariant?.compare_at_price ?? priceData?.dataset.compareAtPrice ?? 0
      );
      const surcharge = this.selectedAddons().reduce((sum, { addon }) => sum + Number(addon.price || 0), 0);
      const displayedPrice = this.formatMoney(basePrice + surcharge);

      priceRoot
        .querySelectorAll('.price__regular .price-item--regular, .price__sale .price-item--sale.price-item--last')
        .forEach((price) => {
          price.textContent = displayedPrice;
        });

      if (compareAtPrice > 0) {
        const displayedCompareAtPrice = this.formatMoney(compareAtPrice + surcharge);
        priceRoot.querySelectorAll('.price__sale .price-item--regular').forEach((price) => {
          price.textContent = displayedCompareAtPrice;
        });
      }
    }
  }

  const instances = new Map();

  const initAll = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.dataset.consoleAddonsReady === 'true') return;
      const instance = new ConsoleAddons(root);
      if (instance.connect()) instances.set(instance.formId, instance);
    });
  };

  const getForForm = (form) => {
    initAll();
    // `form.id` can be shadowed by the product variant input named `id`.
    // Read the attribute directly so the add-on instance is resolved reliably.
    const formId = typeof form === 'string' ? form : form?.getAttribute?.('id');
    return formId ? instances.get(formId) || null : null;
  };

  const propertiesFromFormData = (formData) => {
    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)]$/);
      if (match && value !== '') properties[match[1]] = value;
    }
    return properties;
  };

  window.ConsoleAddons = {
    version: 1,
    initAll,
    getForForm,
    propertiesFromFormData,
    buildCartLines({ form, ...options }) {
      return getForForm(form)?.buildLines(options) || null;
    },
  };

  initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
